import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { priorityService } from "@/services/priorityService";
import { useCandidates } from "@/hooks/useCandidates";
import { AssignedUser, Priority, PriorityStatus } from "@/types";
import { PEOPLE_ACTIVE_STATUSES, PEOPLE_BOARD_LIMIT, PersonBucket, PersonRole } from "@/components/people/people-constants";

export interface PersonEntry {
  priority: Priority;
  role: PersonRole;
  status: PriorityStatus;
}

export interface PersonBoard {
  user: AssignedUser;
  focus: PersonEntry[];
  queue: PersonEntry[];
  after: PersonEntry[];
  /** Index of this person's first deliverable in the PPP-ranked list — people sort by it. */
  firstPosition: number;
}

const bucketOf = (status: PriorityStatus): PersonBucket =>
  status === "focus" ? "focus" : status === "queue" ? "queue" : "after";

/**
 * Pure fold over the PPP-ordered list: each deliverable is attached to its DRI first, then its ICs.
 * The same person on the same item only appears once (DRI wins). Order inside each bucket is the
 * list order, i.e. the derived PPP rank.
 */
export function buildPeopleBoard(list: Priority[]): PersonBoard[] {
  const people = new Map<string, PersonBoard>();
  list.forEach((priority, position) => {
    const assignments = [...(priority.assignments ?? [])].sort((a, b) => Number(!!b.isDri) - Number(!!a.isDri));
    const seen = new Set<string>();
    for (const a of assignments) {
      const pid = a.assignedUser.pid;
      if (seen.has(pid)) continue;
      seen.add(pid);
      const board = people.get(pid) ?? { user: a.assignedUser, focus: [], queue: [], after: [], firstPosition: position };
      const status = a.status ?? priority.status;
      if (status === "done") continue; // finished slice on a still-open deliverable is not open work
      board[bucketOf(status)].push({ priority, role: a.isDri ? "DRI" : "IC", status });
      people.set(pid, board);
    }
  });
  return [...people.values()].sort((a, b) => a.firstPosition - b.firstPosition);
}

const matches = (board: PersonBoard, q: string) =>
  board.user.fullName.toLowerCase().includes(q) ||
  [...board.focus, ...board.queue, ...board.after].some(
    (e) => e.priority.code.toLowerCase().includes(q) || e.priority.name.toLowerCase().includes(q),
  );

export function usePeopleBoard() {
  const [search, setSearch] = useState("");
  const board = useQuery({
    queryKey: ["priorities", "people-board"],
    queryFn: () => priorityService.getPriorities({ statuses: PEOPLE_ACTIVE_STATUSES, candidate: false, page: 1, limit: PEOPLE_BOARD_LIMIT }),
  });
  const candidates = useCandidates();

  const people = useMemo(() => buildPeopleBoard(board.data?.data ?? []), [board.data?.data]);
  const q = search.trim().toLowerCase();
  const filtered = q ? people.filter((p) => matches(p, q)) : people;

  return {
    people: filtered,
    totalPeople: people.length,
    candidatesTotal: candidates.data?.total ?? 0,
    truncated: (board.data?.total ?? 0) > (board.data?.data.length ?? 0),
    isLoading: board.isLoading,
    isError: board.isError,
    search,
    setSearch,
  };
}
